// src/containers/PaymentUrlManage.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import PaymentLinkTable from "../components/PaymentLinkTable";
import AddPaymentLinkDialog from "../components/AddPaymentLinkDialog";
import EditPaymentLinkDialog from "../components/EditPaymentDialog"; // 追加
import {
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  useMediaQuery,
  Typography,
  Collapse,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid2";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AppBar from "@mui/material/AppBar";
import Navbar from "../components/Navbar";
import Toolbar from "@mui/material/Toolbar";

const API_URL = process.env.REACT_APP_API_URL;

const PaymentUrlManage = () => {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [links, setLinks] = useState([]);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [currentEditLink, setCurrentEditLink] = useState(null); // 追加
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false); // 削除確認ダイアログのステート
  const [linkToDelete, setLinkToDelete] = useState(null); // 削除対象のリンク
  const [appbarHeight, setAppbarHeight] = useState(0);
  const [openAlert, setOpenAlert] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    fetchPaymentLinks(); // コンポーネントがマウントされた時に送金リンクを取得
    const appbarElement = document.getElementById("appbar");
    if (appbarElement) {
      setAppbarHeight(appbarElement.offsetHeight); // Navbarの高さをstateに保存
    }
  }, []);

  useEffect(() => {
    const appbarElement = document.getElementById("appbar");
    if (appbarElement) {
      setAppbarHeight(appbarElement.offsetHeight);
    }
  }, [loading]);

  // 送金リンクを取得
  const fetchPaymentLinks = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API_URL}/api/payment-url`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLinks(response.data);
      setLoading(false);
      console.log(response.data);
    } catch (error) {
      setLoading(false);
      setErrorMessage("Error fetching payment links");
      setOpenAlert(true);
      setTimeout(() => {
        setOpenAlert(false); // 3秒後にメッセージを非表示
      }, 3000);
    }
  };

  // リンクの追加
  const handleAddPaymentLink = async (newAppName, newLink, newExpiresAt) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${API_URL}/api/payment-url`,
        { app_name: newAppName, link: newLink, expires_at: newExpiresAt },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessage(response.data.message);
      fetchPaymentLinks(); // リンクを再取得
      setModalIsOpen(false); // モーダルを閉じる
      setErrorMessage(""); // エラーメッセージをリセット
    } catch (error) {
      setErrorMessage("Error adding payment URL");
    }
  };

  // リンクの削除
  const handleDelete = async (id) => {
    setLinkToDelete(id); // 削除対象のIDを設定
    setConfirmDeleteOpen(true); // 削除確認ダイアログを表示
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API_URL}/api/payment-url/${linkToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchPaymentLinks(); // リンクを再取得
      setConfirmDeleteOpen(false); // ダイアログを閉じる
      setLinkToDelete(null); // 削除対象をリセット
    } catch (error) {
      setErrorMessage("Error deleting payment URL");
      setConfirmDeleteOpen(false); // ダイアログを閉じる
    }
  };

  // リンクの編集
  const handleEditClick = (link) => {
    setCurrentEditLink(link); // 現在のリンクを設定
    setEditModalIsOpen(true); // 編集モーダルを開く
  };

  // 編集の保存
  const handleEditPaymentLink = async (updatedLink) => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${API_URL}/api/payment-url/${updatedLink.id}`,
        updatedLink,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchPaymentLinks(); // リンクを再取得
      setEditModalIsOpen(false); // モーダルを閉じる
    } catch (error) {
      setErrorMessage("Error updating payment URL");
    }
  };

  return (
    <div className="content">
      {loading ? (
        <Box
          sx={{
            position: "fixed", // 画面に固定
            top: "50%", // 垂直方向の中央
            left: "50%", // 水平方向の中央
            transform: "translate(-50%, -50%)", // 自身の中心を基準に中央に配置
            zIndex: 1100, // 他の要素の上に表示（必要に応じて調整）
          }}
        >
          <CircularProgress size={60} color="primary" />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              position: "fixed", // 固定位置にする
              right: 0, // 画面右端に配置
              bottom: 0, // 画面下端に配置
              zIndex: 1300, // AppBarよりも上に表示
              width: "auto", // 幅を自動にする
              maxWidth: "400px", // 幅を制限（必要に応じて調整）
              padding: 2, // 少し余白を加える
            }}
          >
            <Collapse in={openAlert}>
              <Alert
                variant="filled"
                severity="error"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setOpenAlert(false);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            </Collapse>
          </Box>

          {isSmallScreen ? (
            <AppBar position="fixed" id="appbar">
              <Toolbar>
                <Navbar />
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ flexGrow: 1, ml: 2 }}
                >
                  送金URL管理
                </Typography>
                {/* <Button color="inherit">Logout</Button> */}
              </Toolbar>
            </AppBar>
          ) : (
            <Navbar />
          )}

          {/* {errorMessage && (
                <Alert
                    severity="error"
                    action={
                        <IconButton
                            color="inherit"
                            onClick={() => setErrorMessage('')}
                        >
                            <CloseIcon />
                        </IconButton>
                    }
                >
                    {errorMessage}
                </Alert>
            )} */}

          {isSmallScreen ? (
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              flexDirection={{ xs: "row", sm: "row" }}
              size={12}
              sx={{ marginTop: `${appbarHeight}px` }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setModalIsOpen(true)}
              >
                新しい送金URLを追加
              </Button>
            </Grid>
          ) : (
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              flexDirection="row"
              size={12}
            >
              <h2>送金URL管理</h2>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setModalIsOpen(true)}
              >
                新しい送金URLを追加
              </Button>
            </Grid>
          )}

          <Box className="spacer-lg"></Box>

          <Grid container spacing={2} justifyContent="flex-end">
            <PaymentLinkTable
              links={links}
              onDeleteClick={handleDelete}
              onEditClick={handleEditClick} // 編集用のハンドラを追加
            />
          </Grid>

          {/* 新しい送金URLを追加するモーダル */}
          <AddPaymentLinkDialog
            open={modalIsOpen}
            onClose={() => setModalIsOpen(false)}
            onAdd={handleAddPaymentLink}
            loading={buttonLoading}
          />

          {/* リンク編集用のモーダル */}
          <EditPaymentLinkDialog
            open={editModalIsOpen}
            onClose={() => setEditModalIsOpen(false)}
            link={currentEditLink}
            onEdit={handleEditPaymentLink} // 編集処理を渡す
          />

          {/* 削除確認用のダイアログ */}
          <Dialog
            open={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
          >
            <DialogTitle>削除確認</DialogTitle>
            <DialogContent>
              <p>本当にこのリンクを削除しますか？</p>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setConfirmDeleteOpen(false)}
                color="secondary"
              >
                キャンセル
              </Button>
              <Button onClick={confirmDelete} color="primary">
                削除
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default PaymentUrlManage;
